/**
 * A part's pieces on their source-layer lanes (D22's `sourceLayers`): one
 * row per lane the part uses, each piece a bar from its start for its
 * duration across the part's span (an open-ended piece runs to the end, with
 * a fade). On air, a playhead shows how far into the part playout is.
 *
 * Positions are percentages of the span, set as inline styles — the token
 * palette governs colour, not geometry.
 */
import type { LaneView, PieceView } from '../../lib/playout/viewModel.js';
import { formatLength } from '../../lib/playout/format.js';
import { cn } from '../../lib/utils.js';

export type PieceStripProps = {
  readonly lanes: readonly LaneView[];
  readonly spanMs: number;
  /** The playhead, ms into the part (on air); null for none. */
  readonly progressMs: number | null;
  /** The part's status, for the bars' colour. */
  readonly tone: 'onair' | 'next' | 'plain';
};

function pct(ms: number, spanMs: number): string {
  return `${String(Math.min(100, Math.max(0, (ms / spanMs) * 100)))}%`;
}

function pieceTitle(p: PieceView): string {
  const timing = `${formatLength(p.startMs)}${p.durationMs === null ? ' →' : ` + ${formatLength(p.durationMs)}`}`;
  const flags = [p.adlib ? 'adlib' : null, p.disabled ? 'disabled' : null, p.stopped ? 'stopped' : null].filter((f) => f !== null);
  return `${p.name === '' ? p.pieceType : p.name} (${p.pieceType}) ${timing}${flags.length > 0 ? ` — ${flags.join(', ')}` : ''}`;
}

const BAR_TONE: Readonly<Record<PieceStripProps['tone'], string>> = {
  onair: 'bg-onair text-onair-foreground',
  next: 'bg-next text-next-foreground',
  plain: 'bg-secondary text-secondary-foreground',
};

function pieceState(p: PieceView): 'disabled' | 'stopped' | 'adlib' | 'normal' {
  if (p.disabled) return 'disabled';
  if (p.stopped) return 'stopped';
  return p.adlib ? 'adlib' : 'normal';
}

function PieceBar({ piece: p, spanMs, tone }: { readonly piece: PieceView; readonly spanMs: number; readonly tone: PieceStripProps['tone'] }) {
  const state = pieceState(p);
  const label = p.name === '' ? p.pieceType : p.name;
  return (
    <div
      className={cn(
        'absolute inset-y-0 truncate rounded-sm px-1 text-xs leading-5',
        BAR_TONE[tone],
        p.durationMs === null && 'rounded-r-none',
        state === 'adlib' && 'ring-2 ring-ring',
        (state === 'disabled' || state === 'stopped') && 'line-through opacity-50',
      )}
      style={{ left: pct(p.startMs, spanMs), width: pct(p.durationMs ?? spanMs - p.startMs, spanMs) }}
      title={pieceTitle(p)}
      data-testid="piece-bar"
      data-piece-state={state}
    >
      {label}
    </div>
  );
}

export function PieceStrip({ lanes, spanMs, progressMs, tone }: PieceStripProps) {
  if (lanes.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="piece-strip-empty">
        No pieces
      </p>
    );
  }
  return (
    <div className="relative grid gap-0.5" data-testid="piece-strip">
      {lanes.map((lane) => (
        <div key={lane.id} className="grid grid-cols-6 items-center gap-2" data-testid="piece-lane" data-layer={lane.id}>
          <span className="col-span-1 truncate text-xs text-muted-foreground" title={lane.id}>
            {lane.label}
          </span>
          <div className="relative col-span-5 h-5 rounded-sm bg-muted">
            {lane.pieces.map((p) => (
              <PieceBar key={p.id} piece={p} spanMs={spanMs} tone={tone} />
            ))}
          </div>
        </div>
      ))}
      {progressMs === null ? null : (
        <div className="pointer-events-none absolute inset-0 grid grid-cols-6 gap-2" aria-hidden="true">
          <div className="relative col-span-5 col-start-2">
            <div className="absolute inset-y-0 w-0.5 bg-foreground" style={{ left: pct(progressMs, spanMs) }} data-testid="playhead" />
          </div>
        </div>
      )}
    </div>
  );
}
